import { AmountHistoryEntry, Lease, calculateYearlyIncome } from '../types/leases';
import { CoTenantRole, LeaseObjectType, UnitKind } from '../types/Enums';
import { formatDateOnlyForStorage } from './DateUtils';
import { stairwellLabel } from './stairwellUtils';

/**
 * Pure helpers for leases. Dates are 'YYYY-MM-DD' strings, so lexicographic
 * comparison is chronological and no parsing is needed. The end date is
 * inclusive. The currentLeaseKey rule lives only in mf-functions
 * (`leaseApi` / `leases/reconcilePlan.ts`); the web never computes it.
 */

export type LeaseStatus = 'upcoming' | 'active' | 'ended';

export const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const todayStockholm = (): string => formatDateOnlyForStorage(new Date());

export const leaseStatus = (
    lease: Pick<Lease, 'startDate' | 'endDate'>,
    today: string = todayStockholm()
): LeaseStatus => {
    if (today < lease.startDate) return 'upcoming';
    if (lease.endDate && lease.endDate < today) return 'ended';
    return 'active';
};

export const leaseStatusLabel = (status: LeaseStatus): string => {
    switch (status) {
        case 'upcoming': return 'Kommande';
        case 'active': return 'Aktivt';
        case 'ended': return 'Avslutat';
    }
};

/** True when the two periods share at least one day (open end = infinity). */
export const leasePeriodsOverlap = (
    a: Pick<Lease, 'startDate' | 'endDate'>,
    b: Pick<Lease, 'startDate' | 'endDate'>
): boolean => {
    const aEnd = a.endDate ?? '9999-12-31';
    const bEnd = b.endDate ?? '9999-12-31';
    return a.startDate <= bEnd && b.startDate <= aEnd;
};

/**
 * Apartment and commercial-unit leases both point at buildings/{b}/units/{key};
 * the leaseApi callable sets the type from the unit's kind (units plan D7).
 */
export const UNIT_LEASE_OBJECT_TYPES: LeaseObjectType[] = [LeaseObjectType.Apartment, LeaseObjectType.CommercialUnit];

/** "Garage -1 · Plats 12" — the stored display snapshot for a parking spot lease (the leaseApi callable writes the same string). */
export const parkingSpotLeaseLabel = (areaName: string, spotNumber: string): string => {
    const area = areaName.trim();
    const spot = spotNumber.trim().length > 0 ? `Plats ${spotNumber.trim()}` : 'Plats utan nummer';
    return area.length > 0 ? `${area} · ${spot}` : spot;
};

/** "Källare (-01) · Förråd 12" — the stored display snapshot for a storage unit lease (level display name first). */
export const storageUnitLeaseLabel = (levelDisplayName: string, unitNumber: string): string => {
    const level = levelDisplayName.trim();
    const unit = unitNumber.trim().length > 0 ? `Förråd ${unitNumber.trim()}` : 'Förråd utan nummer';
    return level.length > 0 ? `${level} · ${unit}` : unit;
};

/** "Lägenhet 1203" / "Lokal 3" — the stored display snapshot for a unit lease (plan D7: no level). */
export const unitLeaseLabel = (kind: UnitKind, unitNumber: string): string => {
    const noun = kind === UnitKind.Commercial ? 'Lokal' : 'Lägenhet';
    const number = unitNumber.trim();
    return number.length > 0 ? `${noun} ${number}` : `${noun} utan nummer`;
};

/** Lease object type for a unit of the given kind (plan D7). */
export const leaseObjectTypeForUnitKind = (kind: UnitKind): LeaseObjectType =>
    kind === UnitKind.Commercial ? LeaseObjectType.CommercialUnit : LeaseObjectType.Apartment;

export const leaseObjectLabel = (lease: Pick<Lease, 'object'>): string => {
    const label = lease.object.label?.trim();
    if (label) return label;
    switch (lease.object.type) {
        case LeaseObjectType.ParkingSpot: return 'Parkeringsplats';
        case LeaseObjectType.StorageUnit: return 'Förråd';
        case LeaseObjectType.Apartment: return 'Lägenhet';
        case LeaseObjectType.CommercialUnit: return 'Lokal';
        default: return 'Hyresobjekt';
    }
};

/** "Lägenhet 1203 · Uppg. A": the unit's stairwell is looked up live, never stored on the lease. */
export const leaseObjectLabelWithStairwell = (lease: Pick<Lease, 'object'>, stairwellName: string | undefined): string =>
    stairwellName ? `${leaseObjectLabel(lease)} · ${stairwellLabel(stairwellName)}` : leaseObjectLabel(lease);

/**
 * Number of calendar months in `year` during which the lease is active on at
 * least one day. A lease from 1 March counts 10; an open-ended lease that
 * started earlier counts 12; a lease ending 15 June counts 6.
 */
export const activeMonthsInYear = (lease: Pick<Lease, 'startDate' | 'endDate'>, year: number): number => {
    let months = 0;
    for (let month = 1; month <= 12; month++) {
        const mm = String(month).padStart(2, '0');
        const monthStart = `${year}-${mm}-01`;
        // "31" is a safe lexicographic upper bound for every month.
        const monthEnd = `${year}-${mm}-31`;
        if (leasePeriodsOverlap(lease, { startDate: monthStart, endDate: monthEnd })) {
            months += 1;
        }
    }
    return months;
};

/** Adds `months` calendar months to a YYYY-MM-DD date, clamping the day to the target month. */
export const addMonths = (date: string, months: number): string => {
    const [y, m, d] = date.split('-').map(Number);
    const target = new Date(Date.UTC(y, m - 1 + months, 1));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    const day = Math.min(d, lastDay);
    return `${target.getUTCFullYear()}-${String(target.getUTCMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Last day to terminate a fixed-term lease with a notice period: `endDate`
 * minus `noticeMonths`. Null for open-ended leases or without a notice period.
 */
export const noticeDeadline = (lease: Pick<Lease, 'endDate' | 'noticeMonths'>): string | null =>
    lease.endDate && lease.noticeMonths && lease.noticeMonths > 0 ? addMonths(lease.endDate, -lease.noticeMonths) : null;

export interface AmountPeriod {
    from: string;
    /** Inclusive; absent = open-ended. */
    to?: string;
    amount: number;
}

/**
 * The rent over the lease's life as consecutive periods, from `amountHistory`
 * (sorted by `from`) with the current `amount` for the newest period. A lease
 * without history is one period at the current amount. Entries before the
 * start date are clamped to it.
 */
export const amountPeriods = (lease: Pick<Lease, 'startDate' | 'endDate' | 'amount' | 'amountHistory'>): AmountPeriod[] => {
    const entries: AmountHistoryEntry[] = Object.values(lease.amountHistory || {})
        .filter(e => e && typeof e.from === 'string' && typeof e.amount === 'number')
        .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
    if (entries.length === 0) return [{ from: lease.startDate, to: lease.endDate, amount: lease.amount }];
    const periods: AmountPeriod[] = [];
    for (let i = 0; i < entries.length; i++) {
        const from = entries[i].from < lease.startDate ? lease.startDate : entries[i].from;
        const next = entries[i + 1];
        const to = next ? addDays(next.from, -1) : lease.endDate;
        // The newest entry always reflects the current amount.
        const amount = next ? entries[i].amount : lease.amount;
        if (lease.endDate && from > lease.endDate) continue;
        // An entry superseded on or before its own start never applied.
        if (to && to < from) continue;
        periods.push(to ? { from, to, amount } : { from, amount });
    }
    return periods.length > 0 ? periods : [{ from: lease.startDate, to: lease.endDate, amount: lease.amount }];
};

const addDays = (date: string, days: number): string => {
    const [y, m, d] = date.split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d + days));
    return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
};

/**
 * Yearly income of a lease in a given year, prorated by active months per
 * rent period, so a rent change mid-year is reflected (units plan, phase 3).
 */
export const leaseIncomeForYear = (lease: Pick<Lease, 'startDate' | 'endDate' | 'amount' | 'interval' | 'amountHistory'>, year: number): number => {
    let total = 0;
    for (const period of amountPeriods(lease)) {
        const months = activeMonthsInYear({ startDate: period.from, endDate: period.to }, year);
        if (months === 0) continue;
        total += (calculateYearlyIncome(period.amount, lease.interval) * months) / 12;
    }
    return total;
};

/** Sum of yearly income of the leases active today. */
export const activeYearlyIncome = (leases: Lease[], today: string = todayStockholm()): number =>
    leases
        .filter(lease => leaseStatus(lease, today) === 'active')
        .reduce((sum, lease) => sum + calculateYearlyIncome(lease.amount, lease.interval), 0);

const CO_TENANT_ROLES = Object.values(CoTenantRole) as string[];

/** Type guard for a stored co-tenant role. */
export const isCoTenantRole = (value: unknown): value is CoTenantRole =>
    typeof value === 'string' && CO_TENANT_ROLES.includes(value);

/** True when the tenant is the lease's primary tenant or one of its co-tenants. */
export const leaseInvolvesTenant = (lease: Pick<Lease, 'tenantKey' | 'coTenants'>, tenantKey: string): boolean =>
    lease.tenantKey === tenantKey || !!lease.coTenants?.[tenantKey];

/** True when the tenant is liable for the rent: the primary or a medhyresgäst, not a medboende. */
export const tenantHoldsContract = (lease: Pick<Lease, 'tenantKey' | 'coTenants'>, tenantKey: string): boolean =>
    lease.tenantKey === tenantKey || lease.coTenants?.[tenantKey]?.role === CoTenantRole.ContractHolder;

export interface CoTenantEntry {
    tenantKey: string;
    role: CoTenantRole;
}

/**
 * The lease's co-tenants as a stable list: medhyresgäster first, then medboende,
 * each in plain code-unit key order (the same order iOS `LeaseModel` uses).
 */
export const coTenantEntries = (lease: Pick<Lease, 'coTenants'>): CoTenantEntry[] =>
    Object.entries(lease.coTenants ?? {})
        .map(([tenantKey, entry]) => ({ tenantKey, role: entry.role }))
        .sort((a, b) => {
            if (a.role !== b.role) return a.role === CoTenantRole.ContractHolder ? -1 : 1;
            return a.tenantKey < b.tenantKey ? -1 : a.tenantKey > b.tenantKey ? 1 : 0;
        });

/** The rentable object a panel shows, matched against `lease.object`. */
export type LeasedObjectRef =
    | { type: LeaseObjectType.ParkingSpot; areaKey: string; key: string }
    | { type: LeaseObjectType.StorageUnit; key: string }
    /** Lägenhet or lokal: both unit lease types share `buildings/{b}/units/{key}`. */
    | { type: 'unit'; key: string };

export const leaseIsForObject = (lease: Pick<Lease, 'object'>, ref: LeasedObjectRef): boolean => {
    const object = lease.object;
    switch (ref.type) {
        case LeaseObjectType.ParkingSpot:
            return object.type === LeaseObjectType.ParkingSpot && object.areaKey === ref.areaKey && object.key === ref.key;
        case LeaseObjectType.StorageUnit:
            return object.type === LeaseObjectType.StorageUnit && object.key === ref.key;
        case 'unit':
            return UNIT_LEASE_OBJECT_TYPES.includes(object.type) && object.key === ref.key;
    }
};

/**
 * "Tidigare hyresgäster": the object's ended leases (end date before today),
 * newest end first. Derived from the building's leases, never stored.
 */
export const endedLeasesForObject = <T extends Pick<Lease, 'object' | 'startDate' | 'endDate'>>(
    leases: T[],
    ref: LeasedObjectRef,
    today: string = todayStockholm()
): T[] =>
    leases
        .filter(lease => leaseIsForObject(lease, ref) && leaseStatus(lease, today) === 'ended')
        .sort((a, b) => {
            const aEnd = a.endDate ?? '';
            const bEnd = b.endDate ?? '';
            if (aEnd !== bEnd) return aEnd < bEnd ? 1 : -1;
            return a.startDate < b.startDate ? 1 : a.startDate > b.startDate ? -1 : 0;
        });

/**
 * The object's most recent lease (latest start, whatever its status): a new
 * lease on the object starts from its rent and interval. Null when the object
 * was never rented.
 */
export const latestLeaseForObject = <T extends Pick<Lease, 'object' | 'startDate'>>(leases: T[], ref: LeasedObjectRef): T | null =>
    leases
        .filter(lease => leaseIsForObject(lease, ref))
        .reduce<T | null>((latest, lease) => (!latest || lease.startDate > latest.startDate ? lease : latest), null);

/** Hyresavtal (/leases) filtered to one building, optionally opening one lease. */
export const leasesPath = (buildingKey: string, leaseKey?: string): string => {
    const params = new URLSearchParams({ building: buildingKey });
    if (leaseKey) params.set('lease', leaseKey);
    return `/leases?${params.toString()}`;
};
